import { Role } from '@policyfly/protobuf/pfbingo'
import { ADMIN_ROLES } from '@policyfly/utils/constants'
import { computed } from 'vue'

import { useAuthenticationStore } from '@/stores/authentication'
import { useSettingsStore } from '@/stores/settings'

import type { Permissions, SchemaSettings_Statuses } from '@policyfly/protobuf/pfschema'
import type { ApplicationStatus } from '@policyfly/types/application'
import type { PolicyStatus } from '@policyfly/types/policy'
import type { Ref } from 'vue'

export type PermissionName = keyof Permissions

export type GeneralPermission =
  | 'newApplicant'
  | 'internalUserListView'

export type UserPermission =
  | 'userAdd'
  | 'userRemove'
  | 'userResetPassword'

export type AgencyPermission =
  | 'agencyListView'
  | 'agencyAdd'
  | 'agencyAddAgent'
  | 'agencyRename'
  | 'agencyManage'
  | 'agencyArchive'
  | 'myAgencyView'

export type AttachmentPermission =
  | 'attachmentRename'
  | 'attachmentArchive'
  | 'attachmentUpload'
  | 'attachmentInternal'
  | 'attachmentGroup'

export type TaskPermission =
  | 'taskAdd'
  | 'taskEdit'

export type AppPermission =
  | 'appQualityControlPass'
  | 'appQualityControlFail'
  | 'appQualityControlSkip'
  | 'appOverride'
  | 'appCancel'
  | 'appAdvancePendingIssue'
  | 'appReclaim'
  | 'appCreateEndorsement'
  | 'appStartOver'
  | 'appResumeCancellation'
  | 'appRecheck'
  | 'appActivatePolicy'
  | 'appApproveEndorsement'
  | 'appEditApplication'
  | 'appDeleteApplication'
  | 'appArchiveApplication'
  | 'appBackToDraft'
  | 'appBackToReviewing'
  | 'appDeclineApplication'
  | 'appDiscardEndorsement'
  | 'appDiscardReinstatement'
  | 'appDiscardCancellation'
  | 'appProcessCancellation'
  | 'appAbortCancellation'
  | 'appQuote'
  | 'appEditQuote'
  | 'appQuoteLost'
  | 'appReviewQuotes'
  | 'appIssueBinder'
  | 'appEndAndRestart'
  | 'appIssuePolicy'
  | 'appAddInformation'
  | 'appEditInformation'
  | 'appEditPremium'
  | 'appReplicate'

export type PolicyPermission =
  | 'polCancel'
  | 'polCreateEndorsement'
  | 'polAbortCancellation'
  | 'polProcessReinstatement'
  | 'polEndorsementCorrectionReport'
  | 'polRenew'
  | 'polReclaim'
  | 'polOfferRenewal'
  | 'polDeclineRenewal'
  | 'polRequestCancellation'
  | 'polStartOver'
  | 'polCreateExtension'

export type HomePermission =
  | 'agencyAssignedView'
  | 'homeCoverholderView'
  | 'homeCommentView'
  | 'homePriorityScore'

export type AppActionStatus = ApplicationStatus | 'CANCELLED' | 'QUOTED_REVIEW' | 'QUOTED_SELECTED'
type AppPermissionItem = { status: AppActionStatus, kind: Uppercase<keyof SchemaSettings_Statuses> }
type PolicyPermissionItem = { status: PolicyStatus }

export interface UsePermissionParams {
  /**
   * Determines whether an agent should be treated as an administrator.
   * If not provided then if the user is an administrator in any agency, they will be treated as an administrator.
   */
  isAdministrator?: Ref<boolean>
}

type PermissionItem<T extends PermissionName> = T extends AppPermission
  ? AppPermissionItem
  : T extends PolicyPermission
    ? PolicyPermissionItem
    : never

/**
 * A permission and its item (if applicable),
 * as passed to {@link UsePermissionReturnValue.checkPermission checkPermission}.
 */
export type PermissionFallback<T extends PermissionName> = [permission: T, item?: PermissionItem<T>]

interface UsePermissionReturnValue<T extends PermissionName> {
  /**
   * Checks if the user has a permission.
   *
   * @param permission The permission to check if the user can access.
   * @param item Details about the item being checked for the permission, if applicable.
   */
  checkPermission<U extends T> (
    permission: U,
    item?: PermissionItem<U>
  ): boolean
  /**
   * Checks if the user role matches the provided role(s).
   *
   * @param role Either a single role or an array of roles to check against.
   */
  checkRole (role: Role[] | Role | undefined): boolean
  /**
   * Returns a list of roles which currently have the provided permission.
   *
   * @param permission The permission to check if the role can access.
   * @param item Details about the item being checked for the permission, if applicable.
   */
  listRolesForPermission<U extends T> (
    permission: U,
    item?: PermissionItem<U>
  ): Role[]
}

export function usePermission <T extends PermissionName> (params?: UsePermissionParams): UsePermissionReturnValue<T> {
  const settingsStore = useSettingsStore()
  const authenticationStore = useAuthenticationStore()

  const isAdministrator = computed(() => {
    return params?.isAdministrator?.value ??
      authenticationStore.currentAgencies.some((a) => a.isAdministrator)
  })

  const permissionsRole = computed(() => {
    if (authenticationStore.isSuperuser) return Role.ROLE_PROGRAM_ADMIN
    return authenticationStore.userRole
  })

  const permissions = computed<Partial<Permissions>>(() => {
    if (!permissionsRole.value || !settingsStore.slug) {
      return {}
    }

    const roleConfig = settingsStore.roles.find((r) => r.role === permissionsRole.value)
    if (!roleConfig) {
      console.error(`Role ${Role[permissionsRole.value]} doesn't exist.`)
      return {}
    }

    const rolePermissions = { ...roleConfig.permissions }
    if (permissionsRole.value === Role.ROLE_DISTRIBUTION_PARTNER && isAdministrator.value && 'agencyAdminPermissions' in roleConfig && roleConfig.agencyAdminPermissions) {
      // only assign values that have meaningful overrides, such as `true` or an array with items
      const overridePermissions = Object.fromEntries(
        Object.entries(roleConfig.agencyAdminPermissions as Permissions)
          .filter(([, value]) => value === true || (Array.isArray(value) && value.length)),
      )
      Object.assign(rolePermissions, overridePermissions)
    }

    return rolePermissions
  })

  function checkPermissionForRole<U extends T> (perms: Partial<Permissions>, permission: U, item?: PermissionItem<U>): boolean {
    const value = perms[permission]
    if (Array.isArray(value)) {
      if (!item) return false
      return value.some((p: { kinds: string[], statuses: string[] } | { statuses: string[] }) => {
        if ('kinds' in p && p.kinds.length) {
          if (!('kind' in item) || !item.kind) return false
          if (!p.kinds.includes(item.kind)) return false
        }
        return p.statuses.includes(item.status)
      })
    } else {
      return !!value
    }
  }

  const checkPermission: UsePermissionReturnValue<T>['checkPermission'] = (permission, item) => {
    return checkPermissionForRole(permissions.value, permission, item)
  }

  const checkRole: UsePermissionReturnValue<T>['checkRole'] = (role) => {
    if (!permissionsRole.value) return false
    if (Array.isArray(role)) {
      return !!role.length && role.includes(permissionsRole.value)
    }
    return !!role && role === permissionsRole.value
  }

  const listRolesForPermission: UsePermissionReturnValue<T>['listRolesForPermission'] = (permission, item) => {
    return [...settingsStore.roles.reduce<Set<Role>>((acc, role) => {
      if (role.permissions && checkPermissionForRole(role.permissions, permission, item)) {
        acc.add(role.role)
        if (role.role === Role.ROLE_PROGRAM_ADMIN) {
          for (const r of ADMIN_ROLES) acc.add(r)
        }
      }
      return acc
    }, new Set())]
  }

  return {
    checkPermission,
    checkRole,
    listRolesForPermission,
  }
}
